import React, { forwardRef, useMemo, useRef } from "react";
import { Grid } from "components/Grid";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import Spinner from "components/Spinner";
import CustomStore from "devextreme/data/custom_store";
import { generateFiltersByAcceptingCustomStringInput } from "components/Grid/utils";
import { DataGridRef } from "devextreme-react/data-grid";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import {
  WaterMeterActivityStatus,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
  WaterSubscriberType,
} from "types/dto";
import { TApartmentGridRow } from "components/Grid/grids/ApartmentsGrid/types";
import { TWaterSubscribersGridRow } from "components/Grid/grids/WaterSubscribersGrid/types";
import { ActivityStatusBadge } from "components/Grid/grids/WaterMetersGrid/ActivityStatusBadge";
import { getJalaliDate } from "utils/dateTimeTools";
import { GridTypes } from "components/Grid/grids/constants";

type TProps = {
  provinceId: string;
  cityId: string;
  jurisdictionId: string;
  actionButtons?: {
    icon: string;
    hint: string;
    onClick: (row: any) => void;
  }[];
  isLoading?: boolean;
  onRowClick?: (e: TWaterSubscribersGridRow) => void;
  /**
   * Adds right-click -> "view details" / "open in new tab" on each row.
   * Opt-in rather than default: this grid also renders inside chooser
   * modals, where navigating away would discard the user's selection.
   */
  enableDetailsContextMenu?: boolean;
};
const WaterSubscribersGrid = forwardRef<DataGridRef, TProps>((props, ref) => {
  const {
    jurisdictionId,
    provinceId,
    cityId,
    isLoading,
    actionButtons,
    onRowClick,
    enableDetailsContextMenu,
  } = props;

  // Called unconditionally (rules of hooks); the prop only decides whether
  // the resulting menu is handed to the grid.
  const detailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/subscribers/details/${row.id}` : undefined
  );
  const { t } = useTranslation();

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: "id",
      load: async (loadOption) => {
        if (loadOption.filter) {
          loadOption.filter = generateFiltersByAcceptingCustomStringInput(
            loadOption.filter
          );
        }

        const res = await netCall({
          method: "POST",
          endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/GetWaterSubscribers`,
          body: {
            loadOptions: { ...loadOption, requireTotalCount: false },
            provinceId: provinceId,
            cityId: cityId,
            jurisdictionId: jurisdictionId,
          },
        });
        return res.results;
      },
    });
  }, [provinceId, cityId, jurisdictionId]);

  const columns = useMemo(() => {
    const _columns: any[] = [
      {
        dataField: "id",
        caption: t("id"),
      },
      {
        dataField: "subscriberName",
        caption: t("subscriberName"),
      },
      {
        dataField: "nationalId",
        caption: t("nationalId"),
      },
      {
        dataField: "phoneNumber",
        caption: t("phoneNumber"),
      },
      {
        dataField: "mobileNumber",
        caption: t("mobileNumber"),
      },
      {
        dataField: "postalCode",
        caption: t("postalCode"),
      },
      {
        dataField: "email",
        caption: t("email"),
      },
      {
        dataField: "identifierId",
        caption: t("subscriberIdentifierId"),
      },
      {
        dataField: "floorNo",
        caption: t("floorNo"),
      },
      {
        dataField: "unitNo",
        caption: t("unitNo"),
      },
      {
        dataField: "address",
        caption: t("address"),
        visible: false,
      },
      {
        dataField: "hasInstalledWaterMeter",
        caption: t("hasInstalledWaterMeter"),
        dataType: "boolean",
      },
      {
        caption: t("waterMeterInfo"),
        columns: [
          {
            dataField: "installedWaterMeter.serialNo",
            caption: t("serialNo"),
          },
          {
            dataField: "installedWaterMeter.modelType",
            caption: t("waterMeterModelType"),
            lookup: {
              dataSource: SortObjectByPropName(
                Enum2Array(WaterMeterModelType),
                "name"
              ),
              valueExpr: "value",
              displayExpr: "name",
            },
          },
          {
            dataField: "installedWaterMeter.locationType",
            caption: t("waterMeterLocationType"),
            lookup: {
              dataSource: SortObjectByPropName(
                Enum2Array(WaterMeterLocationType),
                "name"
              ),
              valueExpr: "value",
              displayExpr: "name",
            },
          },
          {
            dataField: "installedWaterMeter.activityStatus",
            caption: t("activityStatus"),
            cellRender: (cell: any) => (
              <ActivityStatusBadge value={cell.value} />
            ),
          },
          {
            dataField: "installedWaterMeter.batteryPercentage",
            caption: t("batteryPercentage"),
          },
          {
            dataField: "installedWaterMeter.lastReading",
            caption: t("lastReading"),
          },
          {
            dataField: "installedWaterMeter.lastSyncDT",
            caption: t("lastSyncDT"),
            dataType: "dateTime",
            calculateCellValue: (data: any) =>
              getJalaliDate({ date: data?.installedWaterMeter?.lastSyncDT }),
          },
          {
            dataField: "installedWaterMeter.installationDate",
            caption: t("installationDate"),
            dataType: "date",
            calculateCellValue: (data: any) =>
              getJalaliDate({
                date: data?.installedWaterMeter?.installationDate,
              }),
          },
        ],
      },
    ];
    if (actionButtons && actionButtons.length > 0) {
      _columns.push({
        caption: t("actions"),
        visible: true,
        width: 110,
        buttons: actionButtons.map((button) => ({
          icon: button.icon,
          hint: button.hint,
          onClick: (e: any) => button.onClick(e.row.data),
        })),
      });
    }
    return _columns;
  }, [t, actionButtons]);

  return (
    <>
      <div className={"w-full flex gap-x flex-1 min-h-0"}>
        {isLoading && (
          <div className="z-[200] absolute bg-mofidGray-40 backdrop-blur-[2px] bg-opacity-30 top-0 left-0 bottom-0 right-0 flex justify-center items-center rounded-md">
            <p>{t("gettingInformation")}</p>
            <Spinner />
          </div>
        )}

        <Grid
          ref={ref}
          gridKey={"id"}
          dataSource={dataSource}
          storageKey={GridTypes.WaterSubscribers}
          columns={columns}
          rowContextMenu={
            enableDetailsContextMenu ? detailsContextMenu : undefined
          }
          hasSearchPanel={false}
          hasPagination={true}
          hasGroupingFeatures={false}
          hasFilterBuilder={true}
          onRowClick={(e) => {
            e?.data && onRowClick?.(e.data as TWaterSubscribersGridRow);
          }}
          height={"100%"}
        />
      </div>
    </>
  );
});

export default WaterSubscribersGrid;
